import {
  isHalfWidthNumeric,
  isHalfOrFullWidthNumeric,
  isHalfWidthKatakana,
  FieldValidationResult,
  ValidationError,
} from './common-validation';
import { validateRepresentatives } from './company-shared-validation';
import { VALIDATION_MESSAGES, SUPPLIER_MESSAGES } from './validation-messages';
import { VALIDATION_LIMITS } from './validation-limits';

import type {
  SupplierRegistrationForm,
  SupplierDetailForm,
  Address,
  HeadOfficeAddress,
  FactoryContact,
  RawMaterial,
} from '../../types/company.types';

const safeTrim = (input: string | undefined | null): string => {
  if (!input || typeof input !== 'string') return '';
  // Normalize Unicode (NFC) and trim
  const nfc = input.normalize('NFC').trim();

  let out = '';
  for (const char of nfc) {
    const code = char.charCodeAt(0);
    const isAsciiControl = (code >= 0x00 && code <= 0x1f) || (code >= 0x7f && code <= 0x9f);
    if (!isAsciiControl) out += char;
  }
  return out;
};

const toNumber = (value: number | string | undefined | null): number | null => {
  if (value === '' || value === undefined || value === null) return null;
  const numValue = typeof value === 'string' ? Number(value) : value;
  return Number.isNaN(numValue) ? null : numValue;
};

// Generic helper for required field validation (no length check)
const validateRequiredField = (value: string, requiredMessage: string): FieldValidationResult => {
  const normalizedValue = safeTrim(value);

  if (!normalizedValue) {
    return {
      isValid: false,
      error: requiredMessage,
    };
  }

  return { isValid: true };
};

// Helper functions for email validation
const isValidEmailFormat = (email: string, at: number): boolean => {
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);

  if (!local || !domain) return false;
  if (/\s/.test(local) || /\s/.test(domain)) return false;
  if (local.length > 64) return false;

  return isValidDomain(domain);
};

const isValidDomain = (domain: string): boolean => {
  const lastDot = domain.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === domain.length - 1) return false;

  const labels = domain.split('.');
  return labels.every(isValidDomainLabel);
};

const isValidDomainLabel = (label: string): boolean => {
  if (label.length === 0 || label.length > 63) return false;
  if (!/^[A-Za-z0-9-]+$/.test(label)) return false;
  if (label.startsWith('-') || label.endsWith('-')) return false;
  return true;
};

const isValidEmail = (raw: string): boolean => {
  const email = safeTrim(raw);
  if (!email) return false;

  const at = email.indexOf('@');
  if (at <= 0 || at !== email.lastIndexOf('@')) return false;

  return isValidEmailFormat(email, at);
};

// Individual field validations
export const validateGaliSupplierCode = (
  code: string,
  required: boolean = false
): FieldValidationResult => {
  const normalizedCode = safeTrim(code);

  if (!normalizedCode) {
    if (required) {
      return {
        isValid: false,
        error: 'Gali取引先コードは必須入力項目です。',
      };
    }
    return { isValid: true }; // Optional field
  }

  if (!isHalfWidthNumeric(normalizedCode)) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.GALI_CODE_FORMAT,
    };
  }

  return { isValid: true };
};

export const validateOfficialName = (name: string): FieldValidationResult => {
  return validateRequiredField(name, VALIDATION_MESSAGES.OFFICIAL_NAME_REQUIRED);
};

export const validateAbbreviatedName = (name: string): FieldValidationResult => {
  return validateRequiredField(name, VALIDATION_MESSAGES.ABBREVIATED_NAME_REQUIRED);
};

export const validateSupplierNameKatakana = (
  name: string,
  required: boolean = false
): FieldValidationResult => {
  const normalizedName = safeTrim(name);

  if (!normalizedName) {
    if (required) {
      return {
        isValid: false,
        error: SUPPLIER_MESSAGES.SUPPLIER_NAME_KATAKANA_REQUIRED,
      };
    }
    return { isValid: true };
  }

  if (!isHalfWidthKatakana(normalizedName)) {
    return {
      isValid: false,
      error: SUPPLIER_MESSAGES.SUPPLIER_NAME_KATAKANA_FORMAT,
    };
  }

  return { isValid: true };
};

export const validateSupplierType = (type?: string): FieldValidationResult => {
  if (!type) {
    return {
      isValid: false,
      error: SUPPLIER_MESSAGES.SUPPLIER_TYPE_REQUIRED,
    };
  }

  return { isValid: true };
};

export const validateSupplierCategory = (category?: string): FieldValidationResult => {
  if (!category) {
    return {
      isValid: false,
      error: SUPPLIER_MESSAGES.SUPPLIER_CATEGORY_REQUIRED,
    };
  }

  return { isValid: true };
};

// Helper functions for address validation
const validateSinglePostalCode = (
  postalCode: string,
  fieldName: string,
  requiredMessage: string,
  formatMessage: string,
  expectedLength: number,
  halfWidthErrorMessage: string,
  errors: ValidationError[]
): void => {
  if (!postalCode) {
    errors.push({ field: fieldName, message: requiredMessage });
    return;
  }

  if (!isHalfWidthNumeric(postalCode)) {
    errors.push({
      field: fieldName,
      message: halfWidthErrorMessage,
    });
    return;
  }

  if (postalCode.length !== expectedLength) {
    errors.push({ field: fieldName, message: formatMessage });
  }
};

const validatePostalCodes = (address: Address, prefix: string, errors: ValidationError[]): void => {
  const postalCode1 = safeTrim(address.postalCode1);
  validateSinglePostalCode(
    postalCode1,
    `${prefix}.postalCode1`,
    VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE1_REQUIRED,
    VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE1_FORMAT,
    VALIDATION_LIMITS.POSTAL_CODE.LENGTH_1,
    VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE1_HALF_WIDTH,
    errors
  );

  const postalCode2 = safeTrim(address.postalCode2);
  validateSinglePostalCode(
    postalCode2,
    `${prefix}.postalCode2`,
    VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE2_REQUIRED,
    VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE2_FORMAT,
    VALIDATION_LIMITS.POSTAL_CODE.LENGTH_2,
    VALIDATION_MESSAGES.CURRENT_ADDRESS_POSTAL_CODE2_HALF_WIDTH,
    errors
  );
};

const validateAddressFields = (
  address: Address,
  prefix: string,
  errors: ValidationError[]
): void => {
  const address1 = safeTrim(address.address1);
  if (!address1) {
    errors.push({
      field: `${prefix}.address1`,
      message: VALIDATION_MESSAGES.CURRENT_ADDRESS_ADDRESS1_REQUIRED,
    });
  }

  const address2 = safeTrim(address.address2);
  if (!address2) {
    errors.push({
      field: `${prefix}.address2`,
      message: VALIDATION_MESSAGES.CURRENT_ADDRESS_ADDRESS2_REQUIRED,
    });
  }
};

const validatePhoneFax = (address: Address, prefix: string, errors: ValidationError[]): void => {
  const phone = safeTrim(address.phone);
  if (!phone) {
    errors.push({
      field: `${prefix}.phone`,
      message: VALIDATION_MESSAGES.PHONE_REQUIRED,
    });
  } else if (!isHalfOrFullWidthNumeric(phone)) {
    errors.push({
      field: `${prefix}.phone`,
      message: VALIDATION_MESSAGES.PHONE_FORMAT,
    });
  }

  const fax = safeTrim(address.fax);
  if (!fax) {
    errors.push({
      field: `${prefix}.fax`,
      message: VALIDATION_MESSAGES.FAX_REQUIRED,
    });
  } else if (!isHalfOrFullWidthNumeric(fax)) {
    errors.push({
      field: `${prefix}.fax`,
      message: VALIDATION_MESSAGES.FAX_FORMAT,
    });
  }
};

// Address validation (reuse from customer)
export const validateAddress = (address: Address, prefix: string): ValidationError[] => {
  const errors: ValidationError[] = [];

  validatePostalCodes(address, prefix, errors);
  validateAddressFields(address, prefix, errors);
  validatePhoneFax(address, prefix, errors);

  return errors;
};

// Head office address validation (without phone/fax)
export const validateHeadOfficeAddress = (
  address: HeadOfficeAddress,
  prefix: string
): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Postal Code validation using the same helper
  const postalCode1 = safeTrim(address.postalCode1);
  validateSinglePostalCode(
    postalCode1,
    `${prefix}.postalCode1`,
    VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE1_REQUIRED,
    VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE1_FORMAT,
    VALIDATION_LIMITS.POSTAL_CODE.LENGTH_1,
    VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE1_HALF_WIDTH,
    errors
  );

  const postalCode2 = safeTrim(address.postalCode2);
  validateSinglePostalCode(
    postalCode2,
    `${prefix}.postalCode2`,
    VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE2_REQUIRED,
    VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE2_FORMAT,
    VALIDATION_LIMITS.POSTAL_CODE.LENGTH_2,
    VALIDATION_MESSAGES.HEAD_OFFICE_POSTAL_CODE2_HALF_WIDTH,
    errors
  );

  // Address fields validation
  validateHeadOfficeAddressFields(address, prefix, errors);

  return errors;
};

const validateHeadOfficeAddressFields = (
  address: HeadOfficeAddress,
  prefix: string,
  errors: ValidationError[]
): void => {
  const address1 = safeTrim(address.address1);
  if (!address1) {
    errors.push({
      field: `${prefix}.address1`,
      message: VALIDATION_MESSAGES.HEAD_OFFICE_ADDRESS1_REQUIRED,
    });
  }

  const address2 = safeTrim(address.address2);
  if (!address2) {
    errors.push({
      field: `${prefix}.address2`,
      message: VALIDATION_MESSAGES.HEAD_OFFICE_ADDRESS2_REQUIRED,
    });
  }
};

// Representatives validation - using shared function from company-shared-validation

// Helper for postal code validation
const validateOptionalPostalCode = (
  code: string,
  expectedLength: number,
  errorMessage: string
): FieldValidationResult => {
  const normalizedCode = safeTrim(code);

  if (normalizedCode) {
    if (!isHalfWidthNumeric(normalizedCode) || normalizedCode.length !== expectedLength) {
      return {
        isValid: false,
        error: errorMessage,
      };
    }
  }
  return { isValid: true };
};

export const validateFactoryPostalCode1 = (code: string): FieldValidationResult => {
  return validateOptionalPostalCode(
    code,
    VALIDATION_LIMITS.POSTAL_CODE.LENGTH_1,
    VALIDATION_MESSAGES.FACTORY_POSTAL_CODE1_FORMAT
  );
};

export const validateFactoryPostalCode2 = (code: string): FieldValidationResult => {
  return validateOptionalPostalCode(
    code,
    VALIDATION_LIMITS.POSTAL_CODE.LENGTH_2,
    VALIDATION_MESSAGES.FACTORY_POSTAL_CODE2_FORMAT
  );
};

export const validateFactoryPhone = (phone: string): FieldValidationResult => {
  const normalizedPhone = safeTrim(phone);

  if (normalizedPhone) {
    if (!isHalfOrFullWidthNumeric(normalizedPhone)) {
      return {
        isValid: false,
        error: VALIDATION_MESSAGES.FACTORY_PHONE_FORMAT,
      };
    }
  }
  return { isValid: true };
};

export const validateFactoryFax = (fax: string): FieldValidationResult => {
  const normalizedFax = safeTrim(fax);

  if (normalizedFax) {
    if (!isHalfOrFullWidthNumeric(normalizedFax)) {
      return {
        isValid: false,
        error: VALIDATION_MESSAGES.FACTORY_FAX_FORMAT,
      };
    }
  }
  return { isValid: true };
};

export const validateContactOfficerPhone = (phone: string): FieldValidationResult => {
  const normalizedPhone = safeTrim(phone);

  if (normalizedPhone) {
    if (!isHalfOrFullWidthNumeric(normalizedPhone)) {
      return {
        isValid: false,
        error: VALIDATION_MESSAGES.CONTACT_OFFICER_PHONE_FORMAT,
      };
    }
  }
  return { isValid: true };
};

export const validateContactOfficerEmail = (email: string): FieldValidationResult => {
  const normalizedEmail = safeTrim(email);

  if (normalizedEmail) {
    if (!isValidEmail(normalizedEmail)) {
      return {
        isValid: false,
        error: VALIDATION_MESSAGES.CONTACT_OFFICER_EMAIL_INVALID,
      };
    }
  }
  return { isValid: true };
};

export const validateContactOfficerFax = (fax: string): FieldValidationResult => {
  const normalizedFax = safeTrim(fax);

  if (normalizedFax) {
    if (!isHalfOrFullWidthNumeric(normalizedFax)) {
      return {
        isValid: false,
        error: VALIDATION_MESSAGES.CONTACT_OFFICER_FAX_FORMAT,
      };
    }
  }
  return { isValid: true };
};

// Qualified personnel validation (all must be non-negative integers)
export const validateQualifiedPersonnelCount = (
  count: number | string,
  fieldName: string
): FieldValidationResult => {
  if (count === '' || count === undefined || count === null) {
    return { isValid: true };
  }

  const numValue = toNumber(count);
  if (numValue === null) {
    return {
      isValid: false,
      error: `${fieldName}${VALIDATION_MESSAGES.QUALIFIED_PERSONNEL_MUST_BE_NUMBER}`,
    };
  }

  if (numValue < 0) {
    return {
      isValid: false,
      error: `${fieldName}${VALIDATION_MESSAGES.QUALIFIED_PERSONNEL_MUST_BE_NON_NEGATIVE}`,
    };
  }

  if (!Number.isInteger(numValue)) {
    return {
      isValid: false,
      error: `${fieldName}${VALIDATION_MESSAGES.QUALIFIED_PERSONNEL_MUST_BE_INTEGER}`,
    };
  }

  return { isValid: true };
};

export const validateMixerCardinality = (cardinality: number | string): FieldValidationResult => {
  const numValue = toNumber(cardinality);

  if (cardinality && numValue === null) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.MIXER_CARDINALITY_MUST_BE_NUMBER,
    };
  }

  if (cardinality && numValue !== null && numValue < 0) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.MIXER_CARDINALITY_MUST_BE_NON_NEGATIVE,
    };
  }

  if (cardinality && numValue !== null && !Number.isInteger(numValue)) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.MIXER_CARDINALITY_MUST_BE_INTEGER,
    };
  }

  return { isValid: true };
};

// Transport vehicles validation (all must be non-negative integers)
export const validateTransportVehicleCount = (
  count: number | string,
  fieldName: string
): FieldValidationResult => {
  // Skip validation if empty (optional field)
  if (count === '' || count === undefined || count === null) {
    return { isValid: true };
  }

  const numValue = toNumber(count);
  if (numValue === null) {
    return {
      isValid: false,
      error: `${fieldName}${VALIDATION_MESSAGES.TRANSPORT_VEHICLE_MUST_BE_NUMBER}`,
    };
  }

  if (numValue < 0) {
    return {
      isValid: false,
      error: `${fieldName}${VALIDATION_MESSAGES.TRANSPORT_VEHICLE_MUST_BE_NON_NEGATIVE}`,
    };
  }

  if (!Number.isInteger(numValue)) {
    return {
      isValid: false,
      error: `${fieldName}${VALIDATION_MESSAGES.TRANSPORT_VEHICLE_MUST_BE_INTEGER}`,
    };
  }

  return { isValid: true };
};

export const validateCementMaker = (maker: string): FieldValidationResult => {
  const normalizedMaker = safeTrim(maker);

  if (!normalizedMaker) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.CEMENT_MAKER_REQUIRED,
    };
  }
  return { isValid: true };
};

// Main form validation
// Helper to validate a single validation result
const addValidationResult = (
  result: FieldValidationResult,
  field: string,
  errors: ValidationError[]
): void => {
  if (!result.isValid) {
    errors.push({ field, message: result.error });
  }
};

// Helper functions for supplier form validation - consolidated for both registration and detail forms
const validateCompanyBasicInfo = (
  form: SupplierRegistrationForm | SupplierDetailForm,
  errors: ValidationError[],
  isRegistration: boolean = false
): void => {
  // Company Type validation (only for registration form)
  if (isRegistration && (form as SupplierRegistrationForm).companyType !== '仕入先') {
    errors.push({ field: 'companyType', message: SUPPLIER_MESSAGES.COMPANY_TYPE_MUST_BE_SUPPLIER });
  }

  // Supplier Type validation - REQUIRED for supplier
  const supplierType = isRegistration
    ? (form as SupplierRegistrationForm).supplierType
    : (form as SupplierDetailForm).supplierType;

  addValidationResult(validateSupplierType(supplierType), 'supplierType', errors);

  // Gali code is OPTIONAL but if provided must be valid
  const galiResult = validateGaliSupplierCode(form.galiSupplierCode || '', false);
  if (!galiResult.isValid) {
    errors.push({ field: 'galiSupplierCode', message: galiResult.error });
  }

  // Supplier Category validation - REQUIRED
  const supplierCategory = isRegistration
    ? (form as SupplierRegistrationForm).supplierCategory
    : (form as SupplierDetailForm).supplierCategory;

  if (!supplierCategory) {
    const message = isRegistration
      ? SUPPLIER_MESSAGES.SUPPLIER_CATEGORY_REQUIRED
      : SUPPLIER_MESSAGES.SUPPLIER_CATEGORY_DETAIL_REQUIRED;
    errors.push({ field: 'supplierCategory', message });
  }
};

const validateCompanyNames = (
  form: SupplierRegistrationForm | SupplierDetailForm,
  errors: ValidationError[]
): void => {
  // Official name is REQUIRED - check first
  if (!form.officialName?.trim()) {
    errors.push({
      field: 'officialName',
      message: VALIDATION_MESSAGES.OFFICIAL_NAME_REQUIRED,
    });
  } else {
    const officialResult = validateOfficialName(form.officialName);
    if (!officialResult.isValid) {
      errors.push({ field: 'officialName', message: officialResult.error });
    }
  }

  // Abbreviated name is REQUIRED - check first
  if (!form.abbreviatedName.trim()) {
    errors.push({
      field: 'abbreviatedName',
      message: VALIDATION_MESSAGES.ABBREVIATED_NAME_REQUIRED,
    });
  } else {
    const abbreviatedResult = validateAbbreviatedName(form.abbreviatedName);
    if (!abbreviatedResult.isValid) {
      errors.push({ field: 'abbreviatedName', message: abbreviatedResult.error });
    }
  }

  // Supplier name katakana is REQUIRED - check first
  if (!form.supplierNameKatakana?.trim()) {
    errors.push({
      field: 'supplierNameKatakana',
      message: VALIDATION_MESSAGES.COMPANY_NAME_KANA_REQUIRED,
    });
  } else {
    const katakanaResult = validateSupplierNameKatakana(form.supplierNameKatakana, true);
    if (!katakanaResult.isValid) {
      errors.push({ field: 'supplierNameKatakana', message: katakanaResult.error });
    }
  }
};

// Generic helper function to validate a single field using a validator function
const validateFieldWithValidator = (
  value: string | undefined,
  validator: (value: string) => FieldValidationResult,
  fieldName: string,
  errors: ValidationError[]
): void => {
  if (!value) return;

  const result = validator(value);
  if (!result.isValid) {
    errors.push({
      field: fieldName,
      message: result.error,
    });
  }
};

// Helper function to validate a single factory field
const validateFactoryField = (
  value: string | undefined,
  validator: (value: string) => FieldValidationResult,
  fieldName: string,
  errors: ValidationError[]
): void => {
  validateFieldWithValidator(value, validator, fieldName, errors);
};

// Separate methods instead of using boolean flag - removed validateFactoryInfo dispatcher function
const validateRegistrationFactoryInfo = (
  form: SupplierRegistrationForm,
  errors: ValidationError[]
): void => {
  const factoryAddress = form.factoryAddress;
  if (!factoryAddress) return;

  validateFactoryField(
    factoryAddress.postalCode1,
    validateFactoryPostalCode1,
    'factoryAddress.postalCode1',
    errors
  );

  validateFactoryField(
    factoryAddress.postalCode2,
    validateFactoryPostalCode2,
    'factoryAddress.postalCode2',
    errors
  );
};

const validateDetailFactoryInfo = (form: SupplierDetailForm, errors: ValidationError[]): void => {
  validateFactoryField(
    form.factoryPostalCode1,
    validateFactoryPostalCode1,
    'factoryAddress.postalCode1',
    errors
  );
  validateFactoryField(
    form.factoryPostalCode2,
    validateFactoryPostalCode2,
    'factoryAddress.postalCode2',
    errors
  );
};

// Helper function to validate a single contact field
const validateContactField = (
  value: string | undefined,
  validator: (value: string) => FieldValidationResult,
  fieldName: string,
  errors: ValidationError[]
): void => {
  validateFieldWithValidator(value, validator, fieldName, errors);
};

// Helper function to validate a single factory contact
const validateSingleFactoryContact = (
  contact: FactoryContact,
  index: number,
  errors: ValidationError[]
): void => {
  validateContactField(
    contact.phone,
    validateContactOfficerPhone,
    `factoryContacts.${index}.phone`,
    errors
  );

  validateContactField(
    contact.fax,
    validateContactOfficerFax,
    `factoryContacts.${index}.fax`,
    errors
  );

  validateContactField(
    contact.email,
    validateContactOfficerEmail,
    `factoryContacts.${index}.email`,
    errors
  );
};

const validateFactoryContacts = (
  form: SupplierRegistrationForm | SupplierDetailForm,
  errors: ValidationError[]
): void => {
  // Factory contacts validation (all optional)
  if (!form.factoryContacts) return;

  for (const [index, contact] of form.factoryContacts.entries()) {
    validateSingleFactoryContact(contact, index, errors);
  }
};

const validateQualifiedPersonnelForm = (
  form: SupplierRegistrationForm | SupplierDetailForm,
  errors: ValidationError[]
): void => {
  // Qualified personnel validation (all optional, must be non-negative integers)
  const personnelFields = [
    { field: 'professionalEngineers', label: '技術士（建設部門）' },
    { field: 'concreteDiagnosticians', label: 'コンクリート診断士' },
    { field: 'seniorConcreteTechnicians', label: 'コンクリート主任技士' },
    { field: 'concreteTechnicians', label: 'コンクリート技士' },
  ] as const;

  for (const { field, label } of personnelFields) {
    const value = form.qualifiedPersonnel?.[field];
    if (value !== undefined) {
      const result = validateQualifiedPersonnelCount(value, label);
      if (!result.isValid) {
        errors.push({
          field: `qualifiedPersonnel.${field}`,
          message: result.error,
        });
      }
    }
  }
};

const validateMixerInfo = (
  form: SupplierRegistrationForm | SupplierDetailForm,
  errors: ValidationError[]
): void => {
  if (form.mixerInfo?.mixerCardinality) {
    const result = validateMixerCardinality(form.mixerInfo.mixerCardinality);
    if (!result.isValid) {
      errors.push({
        field: 'mixerInfo.mixerCardinality',
        message: result.error,
      });
    }
  }
};

const validateTransportVehicles = (
  form: SupplierRegistrationForm | SupplierDetailForm,
  errors: ValidationError[],
  vehicleLabels: {
    large10t: string;
    medium8t: string;
    size7t: string;
    size5t: string;
    small4t: string;
    size3t: string;
    size2t: string;
  }
): void => {
  // Transport vehicles validation (all optional, must be non-negative integers)
  const vehicleFields = [
    { field: 'large10t', label: vehicleLabels.large10t },
    { field: 'medium8t', label: vehicleLabels.medium8t },
    { field: 'size7t', label: vehicleLabels.size7t },
    { field: 'size5t', label: vehicleLabels.size5t },
    { field: 'small4t', label: vehicleLabels.small4t },
    { field: 'size3t', label: vehicleLabels.size3t },
    { field: 'size2t', label: vehicleLabels.size2t },
  ] as const;

  for (const { field, label } of vehicleFields) {
    const value = form.transportVehicles?.[field];
    if (value !== undefined) {
      const result = validateTransportVehicleCount(value, label);
      if (!result.isValid) {
        errors.push({
          field: `transportVehicles.${field}`,
          message: result.error,
        });
      }
    }
  }
};

// Helper function for registration form transport vehicles with simpler labels
const validateTransportVehiclesForm = (
  form: SupplierRegistrationForm,
  errors: ValidationError[]
): void => {
  const registrationLabels = {
    large10t: '10t',
    medium8t: '8t',
    size7t: '7t',
    size5t: '5t',
    small4t: '4t',
    size3t: '3t',
    size2t: '2t',
  };
  validateTransportVehicles(form, errors, registrationLabels);
};

// Helper function for detail form transport vehicles with descriptive labels
const validateDetailTransportVehicles = (
  form: SupplierDetailForm,
  errors: ValidationError[]
): void => {
  const detailLabels = {
    large10t: '10t',
    medium8t: '8t',
    size7t: '7t',
    size5t: '5t',
    small4t: '4t',
    size3t: '3t',
    size2t: '2t',
  };
  validateTransportVehicles(form, errors, detailLabels);
};

// Helper function to validate cement materials
const validateCementMaterials = (cement: RawMaterial[], errors: ValidationError[]): void => {
  for (const [index, cementItem] of cement.entries()) {
    if (cementItem.manufacturerOrOrigin) {
      const result = validateCementMaker(cementItem.manufacturerOrOrigin);
      if (!result.isValid) {
        errors.push({
          field: `rawMaterials.cement.${index}.manufacturerOrOrigin`,
          message: result.error,
        });
      }
    }
  }
};

const validateRawMaterials = (
  form: SupplierRegistrationForm | SupplierDetailForm,
  errors: ValidationError[]
): void => {
  // Raw materials validation (all optional)
  if (form.rawMaterials?.cement) {
    validateCementMaterials(form.rawMaterials.cement, errors);
  }
};

export const validateSupplierRegistrationForm = (
  form: SupplierRegistrationForm
): { isValid: boolean; errors: ValidationError[] } => {
  const errors: ValidationError[] = [];

  validateCompanyBasicInfo(form, errors, true);
  validateCompanyNames(form, errors);

  // Address validations using helper functions
  const currentAddressErrors = validateAddress(form.currentAddress, 'currentAddress');
  errors.push(...currentAddressErrors);

  const headOfficeAddressErrors = validateHeadOfficeAddress(
    form.headOfficeAddress,
    'headOfficeAddress'
  );
  errors.push(...headOfficeAddressErrors);

  const representativesErrors = validateRepresentatives(form.representatives);
  errors.push(...representativesErrors);

  // Phone validation
  if (!form.currentAddress.phone?.trim()) {
    errors.push({
      field: 'currentAddress.phone',
      message: VALIDATION_MESSAGES.PHONE_REQUIRED,
    });
  } else if (!isHalfOrFullWidthNumeric(form.currentAddress.phone)) {
    errors.push({
      field: 'currentAddress.phone',
      message: VALIDATION_MESSAGES.PHONE_FORMAT,
    });
  }

  validateRegistrationFactoryInfo(form, errors);
  validateFactoryContacts(form, errors);
  validateQualifiedPersonnelForm(form, errors);
  validateMixerInfo(form, errors);
  validateTransportVehiclesForm(form, errors);
  validateRawMaterials(form, errors);

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Supplier detail form validation
export const validateSupplierDetailForm = (
  form: SupplierDetailForm
): { isValid: boolean; errors: ValidationError[] } => {
  const errors: ValidationError[] = [];

  validateCompanyBasicInfo(form, errors, false);
  validateCompanyNames(form, errors);

  // Address validations using helper functions
  const currentAddressErrors = validateAddress(form.currentAddress, 'currentAddress');
  errors.push(...currentAddressErrors);

  const headOfficeAddressErrors = validateHeadOfficeAddress(
    form.headOfficeAddress,
    'headOfficeAddress'
  );
  errors.push(...headOfficeAddressErrors);

  const representativesErrors = validateRepresentatives(form.representatives);
  errors.push(...representativesErrors);

  validateDetailFactoryInfo(form, errors);
  validateFactoryContacts(form, errors);
  validateQualifiedPersonnelForm(form, errors);
  validateMixerInfo(form, errors);
  validateDetailTransportVehicles(form, errors);
  validateRawMaterials(form, errors);

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Helper function to get field error message
export const getSupplierDetailFieldError = (
  fieldName: string,
  errors: ValidationError[]
): string | undefined => {
  const error = errors.find((err) => err.field === fieldName);
  return error?.message;
};

// Helper function to check if field is valid
export const isSupplierDetailFieldValid = (
  fieldName: string,
  errors: ValidationError[]
): boolean => {
  return !errors.some((err) => err.field === fieldName);
};
